import { useApolloClient } from '@apollo/client';
import { isObject } from 'lodash/fp';
import { useCallback } from 'react';
import { type WebPageDataFragment } from '../../../api/fragments/WebPageData';
import {
    DeleteWebPageImageDocument,
    type DeleteWebPageImageMutation,
    type DeleteWebPageImageMutationVariables,
} from '../../../api/mutations/deleteWebPageImage';
import {
    UploadWebPageImageDocument,
    type UploadWebPageImageMutation,
    type UploadWebPageImageMutationVariables,
} from '../../../api/mutations/uploadWebPageImage';
import { WebPageAsset, WebPageBlockType } from '../../../api/types';
import {
    type ColumnBlockMainInput,
    type ImageBlockInput,
    type TextImageBlockInput,
    type WebpageFormikValue,
} from './shared';

const useHandleWebPageAsset = () => {
    const apolloClient = useApolloClient();

    return useCallback(
        async (values: WebpageFormikValue, webPageId?: string, webpage?: WebPageDataFragment) => {
            const promises = [];

            const { mobileBannerImage, bannerImage } = values;

            if (bannerImage instanceof File) {
                promises.push(
                    apolloClient.mutate<UploadWebPageImageMutation, UploadWebPageImageMutationVariables>({
                        mutation: UploadWebPageImageDocument,
                        variables: {
                            upload: bannerImage,
                            webPageAsset: WebPageAsset.Details,
                            webPageId: webpage.id,
                        },
                    })
                );
            } else if (bannerImage === null && isObject(webpage.bannerImage)) {
                promises.push(
                    apolloClient.mutate<DeleteWebPageImageMutation, DeleteWebPageImageMutationVariables>({
                        mutation: DeleteWebPageImageDocument,
                        variables: {
                            webPageAsset: WebPageAsset.Details,
                            webPageId: webpage.id,
                        },
                    })
                );
            }

            if (mobileBannerImage instanceof File) {
                promises.push(
                    apolloClient.mutate<UploadWebPageImageMutation, UploadWebPageImageMutationVariables>({
                        mutation: UploadWebPageImageDocument,
                        variables: {
                            upload: mobileBannerImage,
                            webPageAsset: WebPageAsset.MobileDetails,
                            webPageId: webpage.id,
                        },
                    })
                );
            } else if (mobileBannerImage === null && isObject(webpage.mobileBannerImage)) {
                promises.push(
                    apolloClient.mutate<DeleteWebPageImageMutation, DeleteWebPageImageMutationVariables>({
                        mutation: DeleteWebPageImageDocument,
                        variables: {
                            webPageAsset: WebPageAsset.MobileDetails,
                            webPageId: webpage.id,
                        },
                    })
                );
            }

            values.webPageBlock.forEach((block, blockIndex) => {
                switch (block.type) {
                    case WebPageBlockType.Column: {
                        const columnBlockInput = block as ColumnBlockMainInput;
                        columnBlockInput.columns.forEach((column, columnIndex) => {
                            const columnBlock = webpage.webPageBlock[blockIndex];

                            if (columnBlock.__typename !== 'ColumnWebPageBlock') {
                                throw new Error('BlockType not support');
                            }
                            const columnFragment = columnBlock.columns[columnIndex];
                            if (column.image instanceof File) {
                                if (columnBlock.__typename !== 'ColumnWebPageBlock') {
                                    throw new Error('blockType and input are not same type');
                                }

                                promises.push(
                                    apolloClient.mutate<
                                        UploadWebPageImageMutation,
                                        UploadWebPageImageMutationVariables
                                    >({
                                        mutation: UploadWebPageImageDocument,
                                        variables: {
                                            upload: column.image,
                                            webPageAsset: WebPageAsset.Column,
                                            webPageBlockId: columnBlock.id,
                                            webPageBlockListId: columnFragment.id ? columnFragment.id : null,
                                            webPageId,
                                        },
                                    })
                                );
                            }
                            if (webpage) {
                                const columnBlockData = webpage.webPageBlock.find(block => block.id === columnBlock.id);
                                if (columnBlockData.__typename !== 'ColumnWebPageBlock') {
                                    throw new Error('blockType and input are not same type');
                                }
                                const columnData = columnBlockData.columns[columnIndex];

                                if (!columnData) {
                                    throw new Error('Column not found');
                                }

                                if (column.image === null && isObject(columnData.image)) {
                                    promises.push(
                                        apolloClient.mutate<
                                            DeleteWebPageImageMutation,
                                            DeleteWebPageImageMutationVariables
                                        >({
                                            mutation: DeleteWebPageImageDocument,
                                            variables: {
                                                webPageAsset: WebPageAsset.Column,
                                                webPageBlockId: columnBlockData.id,
                                                webPageBlockListId: columnData.id,
                                                webPageId: webpage.id,
                                            },
                                        })
                                    );
                                }
                            }
                        });
                        break;
                    }

                    case WebPageBlockType.Image: {
                        const imageBlockInput = block as ImageBlockInput;
                        const imageBlock = webpage.webPageBlock[blockIndex];

                        if (imageBlock.__typename !== 'ImageWebPageBlock') {
                            throw new Error('blockType and input are not same type');
                        }

                        if (imageBlockInput.image instanceof File) {
                            promises.push(
                                apolloClient.mutate<UploadWebPageImageMutation, UploadWebPageImageMutationVariables>({
                                    mutation: UploadWebPageImageDocument,
                                    variables: {
                                        upload: imageBlockInput.image,
                                        webPageAsset: WebPageAsset.Image,
                                        webPageBlockId: imageBlock.id,
                                        webPageId,
                                    },
                                })
                            );
                        }
                        if (webpage) {
                            const imageBlockData = webpage.webPageBlock.find(block => block.id === imageBlock.id);
                            if (imageBlockData.__typename !== 'ImageWebPageBlock') {
                                throw new Error('blockType and input are not same type');
                            }

                            if (imageBlock.image === null && isObject(imageBlockData.image)) {
                                promises.push(
                                    apolloClient.mutate<
                                        DeleteWebPageImageMutation,
                                        DeleteWebPageImageMutationVariables
                                    >({
                                        mutation: DeleteWebPageImageDocument,
                                        variables: {
                                            webPageAsset: WebPageAsset.Image,
                                            webPageBlockId: imageBlockData.id,
                                            webPageId: webpage.id,
                                        },
                                    })
                                );
                            }
                        }

                        imageBlockInput.descriptions.forEach((description, descriptionIndex) => {
                            if (description.icon instanceof File) {
                                promises.push(
                                    apolloClient.mutate<
                                        UploadWebPageImageMutation,
                                        UploadWebPageImageMutationVariables
                                    >({
                                        mutation: UploadWebPageImageDocument,
                                        variables: {
                                            upload: description.icon,
                                            webPageAsset: WebPageAsset.ImageDescription,
                                            webPageBlockId: imageBlock.id,
                                            webPageBlockListId: description?.id ? description.id : null,
                                            webPageId,
                                        },
                                    })
                                );
                            }

                            if (webpage) {
                                const imageBlockData = webpage.webPageBlock.find(block => block.id === imageBlock.id);
                                if (imageBlockData.__typename !== 'ImageWebPageBlock') {
                                    throw new Error('blockType and input are not same type');
                                }
                                const descriptionData = imageBlockData.descriptions[descriptionIndex];

                                if (!descriptionData) {
                                    throw new Error('Description not found');
                                }

                                if (description === null && isObject(descriptionData.icon)) {
                                    promises.push(
                                        apolloClient.mutate<
                                            DeleteWebPageImageMutation,
                                            DeleteWebPageImageMutationVariables
                                        >({
                                            mutation: DeleteWebPageImageDocument,
                                            variables: {
                                                webPageAsset: WebPageAsset.ImageDescription,
                                                webPageBlockId: imageBlockData.id,
                                                webPageBlockListId: description.id,
                                                webPageId: webpage.id,
                                            },
                                        })
                                    );
                                }
                            }
                        });
                        break;
                    }

                    case WebPageBlockType.TextImage: {
                        const textImageBlockInput = block as TextImageBlockInput;
                        const typeBlock = webpage.webPageBlock[blockIndex];

                        if (typeBlock.__typename !== 'TextImageWebPageBlock') {
                            throw new Error('blockType and input are not same type');
                        }

                        if (textImageBlockInput.image instanceof File) {
                            promises.push(
                                apolloClient.mutate<UploadWebPageImageMutation, UploadWebPageImageMutationVariables>({
                                    mutation: UploadWebPageImageDocument,
                                    variables: {
                                        upload: textImageBlockInput.image,
                                        webPageAsset: WebPageAsset.TypeImage,
                                        webPageBlockId: typeBlock.id,
                                        webPageId,
                                    },
                                })
                            );
                        }

                        if (webpage) {
                            const typeBlockData = webpage.webPageBlock.find(block => block.id === typeBlock.id);
                            if (typeBlockData.__typename !== 'TextImageWebPageBlock') {
                                throw new Error('blockType and input are not same type');
                            }

                            if (textImageBlockInput.image === null && isObject(typeBlockData.image)) {
                                promises.push(
                                    apolloClient.mutate<
                                        DeleteWebPageImageMutation,
                                        DeleteWebPageImageMutationVariables
                                    >({
                                        mutation: DeleteWebPageImageDocument,
                                        variables: {
                                            webPageAsset: WebPageAsset.TypeImage,
                                            webPageBlockId: typeBlockData.id,
                                            webPageId: webpage.id,
                                        },
                                    })
                                );
                            }
                        }
                        break;
                    }

                    default:
                }
            });

            if (promises.length) {
                await Promise.all(promises);
            }
        },
        [apolloClient]
    );
};

export default useHandleWebPageAsset;
